import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import forOwn from "lodash/forOwn";
import clone from "lodash/clone";
import submit from "submit-form";
import Modal from "../modal-new";
import DuplicateModalFooter from "./duplicate-workout-components/DuplicateModalFooter";

// Added for optimization tweaks, so as to not have as many arrow functions in render
import ProgressionCheckbox from "./duplicate-workout-components/ProgressionCheckbox";
import ProgressionInput from "./duplicate-workout-components/ProgressionInput";
import LoadProgression from "./duplicate-workout-components/LoadProgression";
import { getExerciseNamesList } from "../../utils/progression-filters";
import CustomFilters from "./duplicate-workout-components/CustomFilters";

class DuplicateWeekModal extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      data: {
        cycle: get(context.locals, "params.cycleId", ""),
        program: get(context.locals, "query.program", ""),
        from: props.calendarMode
          ? props.from
          : get(context.locals, "query.week", ""),
        to: [],
      },
      progressions: {},
      checkedFields: {
        loadProgression: false,
        rpeProgression: false,
        repsProgression: false,
        setsProgression: false,
      },
      checkedFilters: {
        allFilter: true,
        primaryFilter: false,
        secondaryFilter: false,
        assistantFilter: false,
        customFilter: [],
      },
      loadSwitch: {
        percent: true,
        units: context.locals.activeUser.units === "imperial" ? "lb" : "kg",
      },
      viewFilters: false,
    };

    this.exercisesList = getExerciseNamesList(
      context.locals.editExercises || context.locals.activeExercises,
      context.locals.query
    );

    this.handleCheck = this.handleCheck.bind(this);
    this.handleFilters = this.handleFilters.bind(this);
    this.handleProgressionInput = this.handleProgressionInput.bind(this);
    this.toggleSelection = this.toggleSelection.bind(this);
    this.handleLoadSwitch = this.handleLoadSwitch.bind(this);
    this.updateCustomFilter = this.updateCustomFilter.bind(this);
    this.handleClearAll = this.handleClearAll.bind(this);
  }

  handleDuplicate(e) {
    e.preventDefault();
    const { progressions, checkedFilters, loadSwitch } = this.state;
    const { calendarMode = false } = this.props;
    const data = { ...this.state.data };

    if (!calendarMode) {
      const weeksWithErrors = this.validateWorkoutLengthByWeek(data);

      if (weeksWithErrors.length > 0) {
        // set the error message and add it to the data sent to the submit function
        //if no data.error defined, no validation message appears
        const errorMessage = `Please ensure that the week(s) you selected have the
        same amount of workouts as the week you are duplicating. \n
        Week(s) with problems: ${weeksWithErrors
          .map((w) => `week ${parseInt(w[0].week)}`)
          .join(", ")}`;
        data.error = errorMessage;
      }
    }

    data.to = data.to.map((week) => {
      const result = {
        week,
        progression: {
          progressionLoad: progressions[`${week}_progression_load`],
          progressionRpe: progressions[`${week}_progression_rpe`],
          progressionSets: progressions[`${week}_progression_sets`],
          progressionReps: progressions[`${week}_progression_reps`],
          progressionFilters: checkedFilters,
          loadSwitch,
        },
      };
      if (calendarMode)
        result["startDate"] = this.props.weeks[week].format("YYYY-MM-DD");
      return result;
    });

    // Ensure each object in the "to" array has both "week" and "progression" properties
    data.to = data.to.filter((item) => item.week && item.progression);

    const calendarBody = {
      calendarMode,
      from: data.from,
      to: data.to,
      proxyAthlete: this.props.proxyAthlete,
    };

    if (this.context.locals.activeUser.organizationId) {
      data.user = this.context.locals.activeUserToRequest._id;
    }

    submit("/actions/cycle/program/duplicateWeek/", {
      method: "POST",
      body: calendarMode ? calendarBody : data,
    });
  }

  /**
   * Validates if the source week has the same amount of workouts that every destination week
   * @param data
   * @returns {Array.<*>} an array with the invalid destination weeks. If no error, returns an empty array
   */
  validateWorkoutLengthByWeek(data) {
    let sourceWeek = null,
      destinationWeeks = [];
    forOwn(this.props.weeks, (week, weekNumber) => {
      if (weekNumber == data.from) {
        sourceWeek = week;
      } else {
        data.to.map((toWeek) => {
          if (weekNumber == parseInt(toWeek) + 1) {
            destinationWeeks.push(week);
          }
        });
      }
    });

    const sourceLength = sourceWeek.length;
    const weeksWithErrors = destinationWeeks.filter((destWeek) => {
      return destWeek.length !== sourceLength;
    });

    return weeksWithErrors;
  }

  shouldDisable(week, weeKHasExercises) {
    // if week === from
    // week contains exercises
    const { from } = this.state.data;
    return Boolean(from == week || weeKHasExercises);
  }

  toggleSelection({ target: { value } }) {
    this.setState((prevState) => {
      const { data } = prevState;
      const { to } = data;

      const index = to.indexOf(value);
      let newTo;
      if (index > -1) {
        newTo = [...to.slice(0, index), ...to.slice(index + 1)];
      } else {
        newTo = [...to, value];
      }

      return {
        data: {
          ...data,
          to: newTo,
        },
      };
    });
  }

  handleProgressionInput({ target: { name, value } }) {
    const {
      locals: { activeUser, activeUserToRequest },
    } = this.context;
    const intensityMeasure = get(
      activeUserToRequest,
      "intensityMeasure",
      "rpe"
    );
    const progressions = clone(this.state.progressions);
    progressions[name] = Number(value);
    if (name.includes("rpe") && intensityMeasure === "rir") {
      progressions[name] = progressions[name] * -1;
    }
    this.setState((prevState) => Object.assign({}, { progressions }));
  }

  handleCheck({ target: { name, checked } }) {
    this.setState((prevState) => {
      let checkedFields = Object.assign({}, prevState.checkedFields);
      checkedFields[name] = checked;
      return { checkedFields };
    });
  }

  handleFilters({ target: { name, checked } }) {
    if (name === "allFilter") {
      this.setState({
        checkedFilters: {
          allFilter: true,
          primaryFilter: false,
          secondaryFilter: false,
          assistantFilter: false,
          customFilter: [],
        },
      });
    } else if (name === "customFilter") {
      this.setState({
        viewFilters: true,
      });
    } else {
      this.setState((prevState) => {
        let checkedFilters = Object.assign({}, prevState.checkedFilters);
        checkedFilters["allFilter"] = false;
        checkedFilters["customFilter"] = [];
        checkedFilters[name] = checked;
        return { checkedFilters };
      });
    }
  }

  updateCustomFilter(action, data) {
    switch (action) {
      case "check":
        // Custom filter checkbox checked, update array
        return this.setState((prevState) => {
          const checkedFilters = Object.assign({}, prevState.checkedFilters, {
            customFilter: data,
          });
          return { checkedFilters };
        });
      case "discard":
        // "Discard", viewFilters off and clear customFilter
        return this.setState((prevState) => {
          const checkedFilters = Object.assign({}, prevState.checkedFilters, {
            customFilter: [],
          });
          return { checkedFilters, viewFilters: false };
        });
      default:
        // "Save" case, viewFilters off, all filters off except customFilter
        return this.setState((prevState) => {
          const checkedFilters = Object.assign({}, prevState.checkedFilters, {
            allFilter: false,
            primaryFilter: false,
            secondaryFilter: false,
            assistantFilter: false,
          });
          return { checkedFilters, viewFilters: false };
        });
    }
  }

  handleLoadSwitch(e) {
    e.preventDefault();

    this.setState(({ loadSwitch }) => ({
      loadSwitch: Object.assign({}, loadSwitch, {
        percent: !loadSwitch.percent,
      }),
    }));
  }

  handleClearAll(e) {
    e.preventDefault();
    this.setState(({ data }) => ({
      progressions: {},
      checkedFields: {
        loadProgression: false,
        rpeProgression: false,
        repsProgression: false,
        setsProgression: false,
      },
      checkedFilters: {
        allFilter: true,
        primaryFilter: false,
        secondaryFilter: false,
        assistantFilter: false,
        customFilter: [],
      },
      data: { ...data, to: [] },
    }));
    document.getElementById("duplicate-week-form")?.reset?.();
  }

  render() {
    const { weeksOrder, duplicateCheck } = this.props;
    const { data, checkedFields, checkedFilters, loadSwitch, progressions } =
      this.state;
    const { from } = data;
    const {
      loadProgression,
      rpeProgression,
      setsProgression,
      repsProgression,
    } = checkedFields;
    const {
      allFilter,
      primaryFilter,
      secondaryFilter,
      assistantFilter,
      customFilter,
    } = checkedFilters;
    const {
      locals: { activeUser, activeUserToRequest },
    } = this.context;
    const intensityMeasure =
      get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
        ? "RPE"
        : "RiR";

    const loadLabel = loadSwitch.percent ? "%" : loadSwitch.units;
    const disabledProgression =
      !loadProgression &&
      !rpeProgression &&
      !repsProgression &&
      !setsProgression;

    if (this.state.viewFilters) {
      return (
        <CustomFilters
          exercises={this.exercisesList}
          customFilter={customFilter}
          updateCustomFilter={this.updateCustomFilter}
        />
      );
    }

    return (
      <form
        onSubmit={(e) => this.handleDuplicate(e)}
        data-noreset
        id="duplicate-week-form"
      >
        <Modal
          className="duplicate-week-modal"
          toClear={["session", "week"]}
          title={`Duplicate Week ${from}`}
        >
          <div className="body">
            <div className="progression-toggle">
              {(!assistantFilter || secondaryFilter || primaryFilter) && (
                <LoadProgression
                  handleCheck={this.handleCheck}
                  handleLoadSwitch={this.handleLoadSwitch}
                  loadSwitch={loadSwitch}
                  checked={loadProgression}
                />
              )}

              <ProgressionCheckbox
                name="repsProgression"
                onChange={this.handleCheck}
                checked={repsProgression}
              >
                Add progressions to Reps
              </ProgressionCheckbox>

              <ProgressionCheckbox
                name="rpeProgression"
                onChange={this.handleCheck}
                checked={rpeProgression}
              >
                Add progressions to {intensityMeasure} (0.5 incr)
              </ProgressionCheckbox>

              <ProgressionCheckbox
                name="setsProgression"
                onChange={this.handleCheck}
                checked={setsProgression}
              >
                Add progressions to Sets
              </ProgressionCheckbox>
            </div>
            <div
              className={`progression-filter ${
                disabledProgression ? " disabled" : ""
              }`}
            >
              <span>Exercise Type Progression Filter</span>
              <ul>
                <ProgressionCheckbox
                  name="allFilter"
                  checked={allFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  All
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="primaryFilter"
                  checked={primaryFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  Primary Lift
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="secondaryFilter"
                  checked={secondaryFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  Variations
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="assistantFilter"
                  checked={assistantFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  Accessories
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="customFilter"
                  checked={customFilter.length > 0}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  Custom filter ({customFilter.length})
                </ProgressionCheckbox>
              </ul>
            </div>

            <div className="progressions-wrapper">
              {!disabledProgression && (
                <div className="progression-workout">
                  {(!assistantFilter || secondaryFilter || primaryFilter) && (
                    <p className={`p2 ${loadProgression}`}>
                      Load ({loadLabel}){" "}
                    </p>
                  )}
                  <p className={`p2 ${rpeProgression}`}>
                    {intensityMeasure} (0.5 incr)
                  </p>
                  <p className={`p2 ${repsProgression}`}>Reps</p>
                  <p className={`p2 ${setsProgression}`}>Sets</p>
                </div>
              )}
              <div className="progression-weeks">
                {duplicateCheck.map((weekHasExercises, week) => {
                  const disabled = this.shouldDisable(
                    week + 1,
                    weekHasExercises
                  );
                  return (
                    <div key={week}>
                      <ProgressionCheckbox
                        value={week}
                        disabled={disabled}
                        onChange={this.toggleSelection}
                        checked={data.to.some((value) => value == week)}
                      />
                      <p className="whitespace-nowrap s1">
                        {this.props.calendarMode ? "" : "Week"}{" "}
                        {weeksOrder[week]}
                      </p>

                      {(!assistantFilter ||
                        secondaryFilter ||
                        primaryFilter) && (
                        <ProgressionInput
                          disabled={disabled}
                          step="0.5"
                          placeholder="Load"
                          name={`${week}_progression_load`}
                          onInput={this.handleProgressionInput}
                          progressions={progressions}
                          unchecked={!loadProgression}
                        />
                      )}
                      <ProgressionInput
                        disabled={disabled}
                        name={`${week}_progression_rpe`}
                        onInput={this.handleProgressionInput}
                        isRpe
                        progressions={progressions}
                        unchecked={!rpeProgression}
                      />
                      <ProgressionInput
                        disabled={disabled}
                        step="1"
                        placeholder="Reps"
                        name={`${week}_progression_reps`}
                        onInput={this.handleProgressionInput}
                        progressions={progressions}
                        unchecked={!repsProgression}
                      />
                      <ProgressionInput
                        disabled={disabled}
                        step="1"
                        placeholder="Sets"
                        name={`${week}_progression_sets`}
                        onInput={this.handleProgressionInput}
                        progressions={progressions}
                        unchecked={!setsProgression}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DuplicateModalFooter
            onClear={this.handleClearAll}
            disabled={data.to.length === 0}
          />
        </Modal>
      </form>
    );
  }
}

DuplicateWeekModal.contextTypes = { locals: PropTypes.object };

export default DuplicateWeekModal;
